<template>
  <div class="version-diff-selector">
    <div class="version-diff-selector__columns">
      <div class="version-diff-selector__column">
        <div class="column-header">
          <h3 class="column-header__title">Available to publish</h3>
          <span class="column-header__count">{{ availableTotalChanges }}</span>
          <button type="button" class="column-header__btn" @click="selectAllSelectable">Add all</button>
        </div>
        <VersionDiffPanel
          :sections="availableSections"
          action-mode="add"
          :show-dependencies="true"
          hide-empty-icon
          :empty-title="
            availableTotalChanges === 0 && selectedChanges.length === 0 ? 'Nothing changed' : 'All changes selected'
          "
          :empty-message="
            availableTotalChanges === 0 && selectedChanges.length === 0
              ? 'There are no publishable changes on this version'
              : ''
          "
          @toggle-entity="toggleEntity"
          @add-section="addSection"
        />
      </div>
      <div class="version-diff-selector__column">
        <div class="column-header">
          <h3 class="column-header__title">To publish</h3>
          <span class="column-header__count">{{ selectedChanges.length }}</span>
          <button type="button" class="column-header__btn" @click="clearSelection">Clear all</button>
        </div>
        <VersionDiffPanel
          :sections="selectedSections"
          action-mode="remove"
          :show-dependencies="true"
          hide-empty-icon
          empty-title="Nothing here yet"
          empty-message="Select changes from the left panel"
          @toggle-entity="toggleEntity"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type PropType, watch } from "vue";

import useEntitySelection from "@/hooks/useEntitySelection";

import type { DiffSection, EntityChange } from "./versionDiff.types";
import VersionDiffPanel from "./VersionDiffPanel.vue";
import useFeatureFlags from "@/hooks/useFeatureFlags";

const props = defineProps({
  sections: {
    type: Array as PropType<DiffSection[]>,
    required: true,
  },
  modifiedEntityIds: {
    type: Object as PropType<Set<string>>,
    default: () => new Set(),
  },
  appId: {
    type: String,
  },
});

const emit = defineEmits<{
  "update:selectedChanges": [changes: EntityChange[]];
}>();

const { flags } = useFeatureFlags(props.appId);

const STATUS_GATED_SECTIONS = new Set(["abTestChanges", "offerChanges"]);

// Build scoped sections where entityId becomes "sectionType::entityId"
// so sections that share IDs (e.g. classes & config rule groups) don't collide.
// PUBLISHED changes are displayed as ADDED ("Created") in the selector.
const scopedSections = computed<DiffSection[]>(() =>
  props.sections
    .map((section) => ({
      ...section,
      changes: section.changes.map((c) => ({
        ...c,
        changeType: c.changeType === "PUBLISHED" ? ("ADDED" as const) : c.changeType,
        entityId: `${section.type}::${c.entityId}`,
        dependenciesWithChangesByType: Object.fromEntries(
          Object.entries(c.dependenciesWithChangesByType).map(([depType, deps]) => [
            depType,
            deps.map((dep) => ({ ...dep, id: `${depType}::${dep.id}` })),
          ])
        ),
      })),
    }))
    .filter((section) => section.changes.length > 0)
);

// Offers linked to abtests (only selectable through abtest dependency)
const offerIdsLinkedToAbtests = computed(() => {
  const ids = new Set<string>();
  const abtestSection = props.sections.find((s) => s.key === "abTestChanges");
  (abtestSection?.changes ?? []).forEach((c) => {
    (c.dependenciesWithChangesByType.offer ?? []).forEach((dep) => ids.add(`offer::${dep.id}`));
  });
  return ids;
});

// Non-PUBLISHED/STOPPED abtests/offers and abtest-linked offers are shown but not individually selectable
const disabledIds = computed(() => {
  const ids = new Set<string>();
  props.sections.forEach((section) => {
    if (STATUS_GATED_SECTIONS.has(section.key)) {
      section.changes.forEach((change) => {
        if (!change.isPublishable) {
          ids.add(`${section.type}::${change.entityId}`);
        }
      });
    }
  });

  if (!flags.value.offerMultiLinkABTest.enabled) {
    offerIdsLinkedToAbtests.value.forEach((id) => ids.add(id));
  }
  return ids;
});

const getDisabledHint = (entityId: string): string => {
  if (offerIdsLinkedToAbtests.value.has(entityId)) {
    return "managed by A/B tests";
  }
  return "not ready to be published";
};

const allChangesRef = computed(() => scopedSections.value.flatMap((s) => s.changes));

// Locked IDs: ARCHIVED changes (pre-selected, non-removable)
const lockedIds = computed(() => {
  const ids = new Set<string>();
  allChangesRef.value.forEach((c) => {
    if (c.changeType === "ARCHIVED") {
      ids.add(c.entityId);
    }
  });
  return ids;
});

const { selectedIds, manuallySelectedIds, toggleEntity, clearSelection, selectedChanges } = useEntitySelection(
  allChangesRef,
  lockedIds
);

const addChanges = (changes: { entityId: string }[]) => {
  const next = new Set(manuallySelectedIds.value);
  changes.forEach((c) => {
    if (!disabledIds.value.has(c.entityId)) {
      next.add(c.entityId);
    }
  });
  manuallySelectedIds.value = next;
};

const addSection = (sectionKey: string) => {
  const section = scopedSections.value.find((s) => s.key === sectionKey);
  if (section) {
    addChanges(section.changes);
  }
};

const selectAllSelectable = () => addChanges(allChangesRef.value);

const decorateWarning = (entityId: string) =>
  props.modifiedEntityIds.has(entityId) ? { warning: true, warningReason: "recently modified" } : {};

const availableSections = computed<DiffSection[]>(() =>
  scopedSections.value
    .map((section) => ({
      ...section,
      changes: section.changes
        .filter((c) => !selectedIds.value.has(c.entityId))
        .filter((c) => c.isPublishable)
        // Order available changes before disabled (dependencies)
        .sort((a, b) => Number(disabledIds.value.has(a.entityId)) - Number(disabledIds.value.has(b.entityId)))
        .map((c) => ({
          ...c,
          ...decorateWarning(c.entityId),
          ...(disabledIds.value.has(c.entityId) && {
            disabled: true,
            disabledReason: getDisabledHint(c.entityId),
          }),
        })),
    }))
    .filter((section) => section.changes.length > 0)
);

const availableTotalChanges = computed(() => availableSections.value.reduce((sum, s) => sum + s.changes.length, 0));

const selectedSections = computed<DiffSection[]>(() =>
  scopedSections.value
    .map((section) => ({
      ...section,
      changes: section.changes
        .filter((c) => selectedIds.value.has(c.entityId))
        .filter((c) => c.isPublishable)
        // Order manually selected changes before auto-selected (dependencies)
        .sort(
          (a, b) =>
            Number(manuallySelectedIds.value.has(b.entityId)) - Number(manuallySelectedIds.value.has(a.entityId))
        )
        .map((c) => ({
          ...c,
          ...decorateWarning(c.entityId),
          ...(lockedIds.value.has(c.entityId) && { locked: true }),
          ...(!manuallySelectedIds.value.has(c.entityId) &&
            !lockedIds.value.has(c.entityId) && {
              locked: true,
              disabledReason: "included as dependency",
            }),
        })),
    }))
    .filter((section) => section.changes.length > 0)
);

watch(
  selectedChanges,
  (newChanges) => {
    emit("update:selectedChanges", newChanges);
  },
  { deep: true, immediate: true }
);
</script>

<style lang="scss" scoped>
.version-diff-selector {
  &__columns {
    display: flex;
    gap: size("L");
  }

  &__column {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .column-header {
    display: flex;
    align-items: center;
    gap: size("S");
    padding: size("S") size("M");
    margin-bottom: size("S");

    &__title {
      font-size: font("M");
      font-weight: 600;
      margin: 0;
    }

    &__count {
      font-size: font("S");
      font-weight: 500;
      color: var(--vd-base-placeholder-color);
    }

    &__btn {
      margin-left: auto;
      background: none;
      border: 1px solid var(--vd-input-border-color);
      border-radius: 6px;
      padding: 4px 12px;
      font-size: font("S");
      color: var(--vd-base-regular-text-color);
      cursor: pointer;
      font-family: inherit;

      &:hover {
        background-color: var(--vd-input-background-color);
      }
    }
  }
}
</style>
